/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 오늘의 홍보학교 컴포넌트
 * 버전 : 1.0
 * 파일명 : AIServices.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * 수정 : 2025-07-02, 강동형 : 저작권 추가, 더보기 버튼 추가
 * 수정 : 2025-07-04, 강동형 : API 에러 수정
 * 수정 : 2025-07-04, 강동형 : 디자인 변경
 * ================================================================ */

import { styled } from '@mui/material'
import { useEffect, useState } from 'react'
import { postBestSchoolsToday } from 'src/api/Main'
import { useMainPageDialogStore } from 'src/stores/DialogStore'
import { useStorageStore } from 'src/stores/StorageStore'
import { getDateDifference } from 'src/utils/DateUtils'
import { NextButton } from '../common/CommonStyled'
import { Icon_Loading, Icon_Robot } from '../common/CommonIcons'
import { useNavigate } from 'react-router-dom'
import gradient_01 from 'src/assets/images/Gradient_01.svg'

const PageWrapper = styled('div')({
  margin: '10px 20px',
  borderRadius: '10px',
  backgroundColor: '#FFF',
  boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.10)',
  paddingBottom:'20px'
})

const TitleWrapper = styled('div')({
  display:'flex',
  justifyContent:'flex-start',
  alignItems:'center',
  fontWeight : '700',
  fontSize: '18px',
  padding:'20px',
  paddingBottom:'10px',
  gap:'5px'
})

const ShowMoreSpan = styled('span')({
  fontSize:'14px',
  fontWeight: '400',
  textDecorationLine:'underline',
  marginLeft:'auto'
})

const ListWrapper = styled('div')({
  display:'flex',
  justifyContent:'center',
  alignItems:'center',
  width:'100%',
  flexDirection:'column',
  padding:'0px 20px',
  gap:'8px'
})

const ListContainer = styled('div')({
  display:'flex',
  justifyContent:'space-between',
  alignItems:'center',

  width:'100%',
  backgroundImage: 'linear-gradient(180deg, #F9F9F9 0%, #F3F3F3 100%)',
  borderRadius:'10px'
})

const LeftGroup = styled('div')({
  display: 'flex',
  gap: '10px', // TypeWrapper와 NameWrapper 간 간격
  alignItems: 'center',
});

const TypeWrapper = styled('div')({
  display:'flex',
  justifyContent:'center',
  alignItems:'center',
  backgroundColor: 'white', 
  color: '#1E9468', 
  padding: '2px 6px', 
  borderRadius: '4px', 
  border:'1px solid #1E9468',
  marginRight:'auto',
  fontSize:'12px',
  minWidth:'46px',
  lineHeight:'normal'
})

const NameWrapper = styled('div')({
  display:'flex',
  marginRight:'auto',
  marginLeft:0
})

const DateWrapper = styled('div')({
  display:'flex',
  marginLeft:'auto',
  paddingLeft:'10px',
  fontSize:'12px',
  opacity:0.5
})

const AIRecommendationWrapper = styled('div')({
  display:'flex',
  justifyContent:'center',
  alignItems:'center',
  gap:'4px',

  borderRadius: '100px',
  background: 'linear-gradient(180deg, #00B5E8 0%, #006986 100%)',
  fontSize:'12px',
  color:'white',
  padding:'3px 8px',
  fontWeight:500,
  lineHeight:'normal'
})

const ListBox = styled('div')({
  display : 'flex',
  flexDirection:'column',
  gap:'10px',

  backgroundImage: 'linear-gradient(180deg, #F9F9F9 0%, #F3F3F3 100%)',
  width:'100%',
  padding:'10px',
  borderRadius:'5px'
})

const SearchPromotionButton = styled('button')({
  color: 'white',
  padding: '10px',
  fontSize: '16px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  width:'210px',
  // height:'42px',
  fontWeight:500,

  borderRadius: '7px',
  border: '2px solid transparent',
  backgroundImage: `
    linear-gradient(270deg, #003E43 0%, #00A9B6 50%, #003E43 100%),
    linear-gradient(270deg, #73C7DF 0%, #FFFFFF 50%, #73C7DF 100%)
  `,
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box', // ✅ 보더와 내부 나눠서 클립
  boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
})

const AIServices = () => {
  const [top3,setTop3] = useState<any[]>([])
  const setField = useMainPageDialogStore((state)=>state.setField)
  const [isLoading,setIsLoading] = useState(false)
  const getUserdata = useStorageStore().getItem('user').data.user_area
  const navigate = useNavigate();
  const [, setFlag] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFlag((prev) => !prev); // 강제로 리렌더 유도
    }, 300);
    return () => clearTimeout(timer); // cleanup
  }, []); 

  const fetchTop3 = async () => {
    try {
      const response = await postBestSchoolsToday();  
      setTop3(response.data); // 받아온 데이터를 상태에 저장
    } catch (error) {
      console.error('top3 불러오기 실패:', error);
    }
  };

  const parseName = (type : string)=>{
    if (type === '일반고등학교') {
      return '일반고'
    } else if (type === '특성화고등학교') {
      return '특성화고'
    } else if (type === '특수목적고등학교') {
      return '특목고'
    } else if (type === '자율고등학교') {
      return '자율고'
    } else if (type === '대학교' || type === '사이버대학(대학)') {
      return '대학교'
    } else if (type === '전문대학' || type === '기능대학' || type === '사이버대학(전문)') {
      return '전문대'
    }
  }

  const handleClick = (e:any)=>{
    setField('isOpen',true)
    setField('selected',{...e, isAi : true})
  }

  const onClickSearch = ()=>{
    setIsLoading(true)
    setTimeout(() => {
      fetchTop3()
    },2000)
  }

  return (
    <PageWrapper>
      <TitleWrapper>
        오늘의 홍보학교
        <AIRecommendationWrapper><Icon_Robot/>AI 추천</AIRecommendationWrapper>
        {Object.keys(getUserdata).length !== 0 &&
        <ShowMoreSpan onClick={()=>navigate('/todays-promotion')}>
          더보기
        </ShowMoreSpan>
        }
      </TitleWrapper>
      <ListWrapper>
        {top3.length !== 0 ? (
          top3.map((item: any, index: number) => (
            <ListBox>
              {item.announcement_title !== null ?
                <div style={{fontSize:'14px'}}>{item.announcement_title}에 적합한 학교</div>:
                <div style={{fontSize:'14px'}}>홍보 효과가 좋은 학교</div>
              }       
              <ListContainer key={index} onClick={() => handleClick(item)}>
                <LeftGroup>
                  <TypeWrapper>{parseName(item.school_type ?? item.education_type)}</TypeWrapper>
                  <NameWrapper>{item.school_name || item.education_name}</NameWrapper>
                </LeftGroup>
                <DateWrapper>
                  D+
                  {item.visited_at
                    ? Math.min(getDateDifference(item.visited_at), 999)
                    : Math.min(getDateDifference(item.establish_date), 999)}
                </DateWrapper>
              </ListContainer>
            </ListBox>
          ))
        ) 
        : 
        <>
          {Object.keys(getUserdata).length === 0 ? 
            <ListContainer style={{justifyContent:'center', flexDirection:'column', padding:'35px 10px', gap:'10px'}}>
            <span style={{fontSize:'16px',color: '#024C61', fontWeight:'bold'}}>내 정보에서 담당 지역을 먼저 설정해주세요.</span>
            <NextButton style={{width:'210px', height:'40px'}} onClick={()=>navigate('/my-info')}>내 정보 페이지 이동</NextButton>
            </ListContainer>
          :
            <ListContainer style={{
              justifyContent:'center', 
              flexDirection:'column',
              padding:'35px 10px',
              gap:'16px', 
              border: '1px solid transparent', 
              backgroundImage: `
                url(${gradient_01}),
                linear-gradient(58deg, #3797C4 0%, #39EFE9 50%, #23C7C2 100%)
              `,
              backgroundSize: 'cover', // ✅ 전체 영역을 채움
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box', // ✅ 보더와 내부 나눠서 클립
              boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
              }}>
            <span style={{fontSize:'16px',color: 'white', fontWeight:'bold'}}>AI가 추천하는 홍보학교</span>
            {isLoading ? 
            <div style={{marginTop:'24px', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', gap:'16px'}}>
              <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(-360deg); }
                }
              `}
              </style>
              <Icon_Loading style={{ animation: 'spin 1s linear infinite', width:'30px',height:'30px' }}/>
              <span style={{color:'#024C61', fontSize:'14px', textAlign:'center'}}>AI-Powered<br/>Search</span>
            </div>
            :
            <SearchPromotionButton onClick={onClickSearch}>홍보 학교 탐색</SearchPromotionButton>
            }
            </ListContainer>
          }
        </>
        }
      </ListWrapper>
    </PageWrapper>
  )
}

export default AIServices